// Client-side attribution memory for the abandoned-checkout email → checkout
// conversion path.
//
// Set when a visitor lands from an abandoned-checkout retry link
// (utm_medium=abandoned_checkout, captured in useAnalytics). Read on the
// checkout return/success page so we can attribute the completed purchase
// back to the original abandoned Stripe session and price.
//
// Stored in sessionStorage so it survives the Stripe embedded-checkout
// round-trip within the same tab but never leaks across tabs/sessions.

const STORAGE_KEY = "abx_attribution";
// Two hours: long enough for a genuine checkout, short enough that a stale
// value from a much earlier retry click doesn't get attached to an
// unrelated future purchase in the same tab.
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface AbandonedCheckoutAttribution {
  /** price_id from the original abandoned checkout (stable across envs). */
  price_id?: string;
  /** Original abandoned Stripe checkout session id (cs_test_… / cs_live_…). */
  stripe_session_id?: string;
  utm_campaign?: string;
  utm_source?: string;
  /** ms epoch when the attribution was captured. */
  ts: number;
}

export function setAbandonedCheckoutAttribution(
  attr: Omit<AbandonedCheckoutAttribution, "ts">,
): void {
  try {
    const payload: AbandonedCheckoutAttribution = { ...attr, ts: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* never let analytics break UX */
  }
}

export function readAbandonedCheckoutAttribution():
  | AbandonedCheckoutAttribution
  | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AbandonedCheckoutAttribution;
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearAbandonedCheckoutAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
